import { KeyboardEvent, useContext, useEffect, useMemo, useState } from "react";

import { FiUser } from "react-icons/fi";

import { useTranslator } from "@eo-locale/react";

import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  VStack,
  useDisclosure,
  useMediaQuery
} from "@/storybook-components";
import PinIcon from "../markers/pin-Icon";

import { EsriContext } from "@/components/places/context/esri-context";
import FilterButton from "@/components/places/esri-map/filter-pin-modal/filter-button";
import { setCurrentPageNumber } from "@/redux/features/place-marker/slice";
import { setFilteredCategoriesData } from "@/redux/features/place-markers/slice";
import { fetchPinDetails } from "@/redux/features/place-markers/thunk";
import { setView } from "@/redux/features/place-right-panel/slice";
import {
  setMapCenterCoordinates,
  setPinDetails
} from "@/redux/features/place/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

export interface Symbols {
  icon: any;
  imgSrc: string;
  value: string;
  name: string;
  category: Category;
  color: string;
}

export interface Category {
  name: string;
  color: string;
  icon: string;
  visible: boolean;
  answered: boolean;
  id: string;
}

type Marker = {
  name: string;
  icon: string;
  visible: boolean;
  answered: boolean;
  id: string;
  color?: string;
};

const FilterPinModal = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isDesktop] = useMediaQuery("(min-width: 768px)");

  const dispatch = useAppDispatch();
  const { translate } = useTranslator();
  const { openUnsavedModal, setNextModalAction } = useContext(EsriContext);
  const { categories: allcategories = [] } = useAppSelector(
    (state) => state.markerCategories
  );

  const projectId = useAppSelector((state) => state.project.project.id);
  const placesId = useAppSelector((state) => state.place.place.id);
  const { responsesCount } = useAppSelector((state) => state.place.place);
  const filteredCategoriesData = useAppSelector(
    (state) => state.placeMarkersSlice.filteredCategoriesData
  );

  const { id: userId } = useAppSelector(
    (state) => state.user.registeredUser.profile
  );

  const { categories, isSelfPin } = filteredCategoriesData;

  const [showMyPins, setShowMyPins] = useState(isSelfPin);
  const [categoryStates, setCategoryStates] = useState({});

  const [hasTogglesTouched, setToggleTouchStatus] = useState(false);
  const rightPanelView = useAppSelector(
    (state) => state.placesRightPanelView.view
  );
  const SORT = { created_at: "desc" };

  const handleFilterClick = () => {
    if (["edit-pin", "create-pin"].includes(rightPanelView) && isDesktop) {
      setNextModalAction(() => onOpen);
      openUnsavedModal();

      return;
    }

    onOpen();
  };

  const handleToggleChange = (id) => {
    setCategoryStates((prevState) => ({
      ...prevState,
      [id]: !prevState[id]
    }));

    setToggleTouchStatus(true);
  };

  const visibleCategories: Array<Marker> = useMemo(() => {
    return allcategories.filter((category) => category.visible);
  }, [allcategories]);

  const hiddenCategoryIds = useMemo(() => {
    return allcategories
      .filter((category) => !category.visible)
      .map((category) => category.id);
  }, [allcategories]);

  useEffect(() => {
    // If no categories are selected, then select all categories toggle
    if (filteredCategoriesData.categories.length === 0) {
      const initialCategoryStates = {};
      visibleCategories.forEach((category) => {
        initialCategoryStates[category.id] = true;
      });

      setCategoryStates(initialCategoryStates);
      setShowMyPins(isSelfPin);
    }
  }, [visibleCategories, filteredCategoriesData]);

  const onCancelOrClose = () => {
    setToggleTouchStatus(false);
    const initialCategoryStates = {};

    // If no categories are selected, then select all categories toggle
    if (filteredCategoriesData.categories.length === 0) {
      visibleCategories.forEach((category) => {
        initialCategoryStates[category.id] = true;
      });

      setCategoryStates(initialCategoryStates);
    } else {
      // If categories are selected, then maintain the state of selected categories
      categories.forEach((id) => {
        initialCategoryStates[id] = true;
      });

      setCategoryStates(initialCategoryStates);
    }

    setShowMyPins(isSelfPin);
    onClose();
  };

  const closeModal = () => {
    setToggleTouchStatus(false);
    onClose();
  };

  const handleFilterButtonClick = () => {
    if (!hasTogglesTouched || !visibleCategories.length) {
      closeModal();
      return;
    }

    if (["view-pin-details", "custom-marker"].includes(rightPanelView)) {
      dispatch(setView("activity-feed"));
      dispatch(setPinDetails({}));
      dispatch(setMapCenterCoordinates({}));
    }
    // Maintain internal status of toggle
    const categoryIds = filteredCategoryIds.length
      ? filteredCategoryIds
      : ["null"];

    // If all toggles are switched on then show hidden pins as well.
    const showAllPins = filteredCategoryIds.length === visibleCategories.length;
    const hiddenAndVisibleCategoryIds = [...categoryIds, ...hiddenCategoryIds];

    dispatch(
      setFilteredCategoriesData({
        categories: showAllPins ? [] : hiddenAndVisibleCategoryIds,
        isSelfPin: showMyPins,
        sortOrder: "newest"
      })
    );

    if (showMyPins) {
      fetchSelfPins(hiddenAndVisibleCategoryIds, showAllPins);
    } else {
      fetchAllOrOtherPins(hiddenAndVisibleCategoryIds, showAllPins);
    }
    dispatch(setCurrentPageNumber(1));
    closeModal();
  };

  const fetchSelfPins = (categoryIds, showAllPins) => {
    dispatch(
      fetchPinDetails({
        projectId,
        placesId,
        filters: {
          user_id: userId,
          category_id: showAllPins ? [] : categoryIds,
          sort: SORT
        }
      })
    );
  };

  const fetchAllOrOtherPins = (categoryIds, showAllPins) => {
    dispatch(
      fetchPinDetails({
        projectId,
        placesId,
        filters: {
          category_id: showAllPins ? [] : categoryIds,
          sort: SORT
        }
      })
    );
  };

  const filteredCategoryIds = useMemo(() => {
    return visibleCategories
      .filter((category) => categoryStates[category.id])
      .map((category) => category.id);
  }, [visibleCategories, categoryStates]);

  const toggleOnAllCategories = () => {
    setToggleTouchStatus(true);

    setShowMyPins(!showMyPins);

    const allCategoryIds = visibleCategories.map((category) => category.id);
    const updatedCategoryStates = {};

    allCategoryIds.forEach((id) => {
      updatedCategoryStates[id] = true;
    });

    setCategoryStates(updatedCategoryStates);
  };

  const showSelfPinToggle = userId && responsesCount > 0;
  const showNoPinsText =
    (!visibleCategories.length && !showSelfPinToggle) || responsesCount == 0;
  const showOtherToggles = responsesCount > 0;

  const handleKeyPress = (event: KeyboardEvent, categoryId?: string) => {
    if (event.key === "Enter") {
      if (categoryId) {
        handleToggleChange(categoryId);
      } else {
        toggleOnAllCategories();
      }
    }
  };

  return (
    <>
      <FilterButton handleFilterClick={handleFilterClick} />
      <Modal
        onClose={onCancelOrClose}
        isOpen={isOpen}
        size={isDesktop ? "md" : "xs"}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{translate("places.pin.filterPins")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack
              spacing="1rem"
              align="stretch"
              height="16.125rem"
              overflowY="auto"
              pt={1}
              pr={1}
            >
              {showSelfPinToggle && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  height="2rem"
                >
                  <Box display="flex" alignItems="center">
                    <FiUser size="1.25rem" />
                    <Box as="span" ml={2}>
                      {translate("places.pin.showOnlyMyPins")}
                    </Box>
                  </Box>
                  <Switch
                    onKeyDown={(event) => handleKeyPress(event)}
                    isChecked={showMyPins}
                    onChange={toggleOnAllCategories}
                    aria-label={translate("places.pin.showOnlyMyPins")}
                  />
                </Box>
              )}
              {showNoPinsText && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="inherit"
                >
                  {translate("places.pin.noPins")}
                </Box>
              )}
              {showOtherToggles &&
                visibleCategories?.map((category, index) => {
                  const pinIconProps = {
                    pinColor: category.color,
                    pinIcon: category.icon,
                    pinId: category.id
                  };
                  return (
                    <Box key={index} display="flex" flexDirection="column">
                      <Box display="flex" justifyContent="space-between">
                        <Box display="flex" alignItems="start">
                          <Box
                            as="span"
                            height="1.25rem"
                            width="1.25rem"
                            mr={2}
                          >
                            <PinIcon {...pinIconProps} />
                          </Box>

                          <Box
                            as="span"
                            lineHeight="1.375rem"
                            wordBreak="break-all"
                            mr="1rem"
                            mt={2}
                          >
                            {category.name}
                          </Box>
                        </Box>
                        <Switch
                          mt={1}
                          onKeyDown={(event: React.KeyboardEvent) =>
                            handleKeyPress(event, category.id)
                          }
                          aria-label={category.name}
                          isChecked={categoryStates[category.id]}
                          onChange={() => handleToggleChange(category.id)}
                        />
                      </Box>
                    </Box>
                  );
                })}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blackAlpha"
              variant="secondary"
              onClick={onCancelOrClose}
            >
              {translate("common.cancel")}
            </Button>
            <Button ml={4} onClick={handleFilterButtonClick}>
              {translate("common.continue")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default FilterPinModal;
